import { downloadBlob } from "./native-shell";
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export class ApiFetchError extends Error {
  readonly status: number;
  readonly responseBody: string;

  constructor(status: number, responseBody: string) {
    super(`API ${status}: ${responseBody}`);
    this.name = "ApiFetchError";
    this.status = status;
    this.responseBody = responseBody;
  }
}

/**
 * Admin APIs answer 403 `privileged_reverification_required` when the admin's
 * second factor is older than the server's window. apiFetch raises this event so
 * one gate in the admin shell can ask for re-verification instead of every
 * screen showing "Could not load".
 */
export const ADMIN_REVERIFICATION_EVENT = "admin-reverification-required";

export function isAdminReverificationRequired(status: number, body: string): boolean {
  if (status !== 403) return false;
  try {
    return JSON.parse(body)?.error === "privileged_reverification_required";
  } catch {
    return false;
  }
}

export type ApiFetchOptions = RequestInit & {
  authToken?: string | null;
};

export function getApiBase(): string {
  return `${BASE}/api`;
}

export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
}

export async function getAuthHeaders(): Promise<Record<string, string>> {
  return {
    "X-Timezone": getBrowserTimezone(),
  };
}

export async function apiFetch<T = any>(path: string, opts?: ApiFetchOptions): Promise<T> {
  const { authToken, ...requestOptions } = opts ?? {};
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Timezone": getBrowserTimezone(),
    ...(opts?.headers as Record<string, string>),
  };

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  const res = await fetch(`${BASE}/api${path}`, {
    ...requestOptions,
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    if (res.status === 429) {
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error === "ai_budget_exceeded") {
          window.dispatchEvent(
            new CustomEvent("ai-budget-exceeded", { detail: { resetsAt: parsed.resetsAt } }),
          );
          const err = new Error(`AI_BUDGET_EXCEEDED`) as Error & { code?: string; resetsAt?: string };
          err.code = "ai_budget_exceeded";
          err.resetsAt = parsed.resetsAt;
          throw err;
        }
      } catch (e: any) {
        if (e?.code === "ai_budget_exceeded") throw e;
      }
    }
    if (isAdminReverificationRequired(res.status, text)) {
      window.dispatchEvent(new CustomEvent(ADMIN_REVERIFICATION_EVENT));
    }
    throw new ApiFetchError(res.status, text);
  }
  return res.json();
}

export async function downloadFile(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = { "X-Timezone": getBrowserTimezone() };
  const res = await fetch(`${BASE}/api${path}`, { credentials: "include", headers });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text}`);
  }
  const blob = await res.blob();
  await downloadBlob(blob, filename);
}
